import { ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { useAppRouter } from '@/routing';
import { cn } from '@/lib/utils';

export interface MenuItem {
  title: string;
  icon?: ReactNode;
  href?: string;
  items?: MenuItem[];
  group?: number;
}

export interface NavMenu {
  title: string;
  items: MenuItem[];
}

const itemClassName =
  'flex min-h-[var(--erp-nav-dropdown-item-height)] w-full cursor-pointer items-center rounded-sm px-2.5 py-1 text-[length:var(--erp-nav-font-size)] font-medium text-slate-700 outline-none transition-colors focus:bg-main/10 focus:text-main data-[state=open]:bg-main/10 data-[state=open]:text-main [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-slate-400';

const contentClassName = 'z-[1300] min-w-[200px] rounded-sm border border-slate-200 bg-white p-1 shadow-sm';

const groupItems = (items: MenuItem[]) => {
  const groupedItems: Record<number, MenuItem[]> = {};

  items.forEach(item => {
    const group = item.group || 0;

    if (!groupedItems[group]) {
      groupedItems[group] = [];
    }

    groupedItems[group].push(item);
  });

  return Object.keys(groupedItems)
    .map(Number)
    .sort((left, right) => left - right)
    .map(group => ({ group, items: groupedItems[group] }));
};

const RecursiveMenuItem = ({ item, onItemClick }: { item: MenuItem; onItemClick: () => void }) => {
  const router = useAppRouter();
  const hasChildren = Boolean(item.items?.length);

  const handleNavigate = () => {
    if (!item.href) {
      onItemClick();
      return;
    }

    onItemClick();
    router.push(item.href);
  };

  const iconElement = item.icon ? (
    <span className='mr-2 flex h-[16px] w-[16px] shrink-0 items-center justify-center text-slate-400 [&>*]:block [&>*]:size-[16px] [&>*]:shrink-0'>
      {item.icon}
    </span>
  ) : (
    <span className='mr-2 flex h-[16px] w-[16px] shrink-0' />
  );

  if (hasChildren) {
    return (
      <DropdownMenuSub>
        <DropdownMenuSubTrigger className={itemClassName}>
          {iconElement}
          <span className='flex-1 truncate'>{item.title}</span>
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent className={contentClassName} sideOffset={6}>
          {item.items?.map((subItem, index) => (
            <RecursiveMenuItem key={`${subItem.title}-${index}`} item={subItem} onItemClick={onItemClick} />
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    );
  }

  return (
    <DropdownMenuItem
      className={itemClassName}
      onSelect={event => {
        event.preventDefault();
        handleNavigate();
      }}
    >
      {iconElement}
      <span className='flex-1 truncate'>{item.title}</span>
    </DropdownMenuItem>
  );
};

export function AritoNavMenu({ title, items }: NavMenu) {
  const [open, setOpen] = useState(false);
  const groupedItems = groupItems(items);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            'flex h-[var(--erp-nav-trigger-height)] shrink-0 items-center gap-0.5 whitespace-nowrap rounded-sm px-1.5 text-[length:var(--erp-nav-font-size)] font-medium text-main-foreground/90 transition-all duration-150',
            'outline-none hover:bg-main-foreground/10 hover:text-main-foreground data-[state=open]:bg-main-foreground/15 data-[state=open]:text-main-foreground'
          )}
          type='button'
        >
          <span>{title}</span>
          <ChevronDown
            className={cn('ml-0.5 size-3 opacity-60 transition-transform duration-200', open && 'rotate-180')}
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='start' className={contentClassName} sideOffset={2}>
        {groupedItems.map(({ group, items: groupMenuItems }, groupIndex) => (
          <div key={group}>
            {groupIndex > 0 && <DropdownMenuSeparator className='mx-1 my-1 bg-slate-100' />}
            {groupMenuItems.map((item, itemIndex) => (
              <RecursiveMenuItem
                key={`${group}-${item.title}-${itemIndex}`}
                item={item}
                onItemClick={() => setOpen(false)}
              />
            ))}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
